import React, { useState, useEffect } from 'react';
import { IntlProvider } from 'react-intl';
import { usePopmenuConfig } from '~/utils/withPopmenuConfig';
import { toReactIntlLocale } from '~/utils/withIntl';

import PopmenuLoading from '../shared/PopmenuLoading';

interface LazyIntlProviderProps {
  adminLocale?: string | null;
  children: React.ReactNode;
}

const LazyIntlProvider = ({ adminLocale, children }: LazyIntlProviderProps) => {
  const { popmenuVersion } = usePopmenuConfig();
  const locale = toReactIntlLocale(adminLocale);
  const [messages, setMessages] = useState<Record<string, string> | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (locale === 'en') {
      setMessages(undefined);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    // Append version to break cache across deployments
    const url = `/locales/admin/${locale.replace('-', '_')}.json?v=${popmenuVersion}`;
    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load translations: ${response.statusText}`);
        }
        return response.json() as Promise<Record<string, string>>;
      })
      .then((translationMessages) => {
        setMessages(translationMessages);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error(`[POPMENU] Failed to load translation messages for locale ${locale} from ${url}`, error);
        setMessages(undefined);
        setIsLoading(false);
      });
  }, [locale, popmenuVersion]);

  if (isLoading) {
    return (
      <PopmenuLoading />
    );
  }

  return (
    <IntlProvider
      defaultLocale="en"
      locale={locale}
      messages={messages}
    >
      {children}
    </IntlProvider>
  );
};

export default LazyIntlProvider;
